import api from '../services/api';
import companiaService, { type Compania } from '../services/companiaService';

async function blobToPngDataUrl(blob: Blob): Promise<string | null> {
  if (!blob.size || (blob.type && blob.type.includes('json'))) return null;
  try {
    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bmp.close?.();
      return null;
    }
    ctx.drawImage(bmp, 0, 0);
    bmp.close?.();
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

async function fetchLogoBlobViaApi(path: string): Promise<string | null> {
  try {
    const { data } = await api.get<Blob>(path, { responseType: 'blob' });
    const blob = data instanceof Blob ? data : new Blob([data as BlobPart]);
    return blobToPngDataUrl(blob);
  } catch {
    return null;
  }
}

/** Logo vía API (archivo subido o URL remota en el servidor). */
export async function fetchLogoPngDataUrl(idCompania: string): Promise<string | null> {
  const path = `/api/Compañias/${encodeURIComponent(idCompania)}/logo-contenido`;
  return fetchLogoBlobViaApi(path);
}

export async function fetchCompaniaParaPdf(): Promise<Compania | null> {
  try {
    const list = await companiaService.getCompanias({ pageNumber: 1, pageSize: 1 });
    if (!list.length) return null;
    return await companiaService.getCompania(list[0].idCompania);
  } catch {
    return null;
  }
}

function normalizarTextoCompania(s: string | undefined | null): string {
  if (s == null || s === '') return '';
  return String(s).replace(/\s+/g, ' ').trim();
}

export function lineasDatosCompania(c: Compania): string[] {
  const out: string[] = [];
  const nombre = normalizarTextoCompania(c.nombre);
  if (nombre) out.push(nombre);
  const cpLoc = [normalizarTextoCompania(c.cp), normalizarTextoCompania(c.localidad)]
    .filter(Boolean)
    .join(' ');
  const dom = [normalizarTextoCompania(c.domicilio), cpLoc].filter(Boolean).join(' — ');
  if (dom) out.push(dom);
  const provN = normalizarTextoCompania(c.provincia);
  let paisN = normalizarTextoCompania(c.pais);
  if (paisN === '.' || paisN === '-') paisN = '';
  const loc = [provN, paisN].filter(Boolean).join(' · ');
  if (loc) out.push(loc);
  const cuit = normalizarTextoCompania(c.cuit);
  if (cuit) out.push(`CUIT: ${cuit}`);
  const cond = normalizarTextoCompania(c.condIva);
  if (cond) out.push(cond);
  const tel = normalizarTextoCompania(c.telefono1);
  if (tel) out.push(`Tel: ${tel}`);
  const mail = normalizarTextoCompania(c.mail1);
  if (mail) out.push(mail);
  return out.slice(0, 8);
}

/** Ancho útil de tabla Libro IVA en PDF apaisado A4 (pt). */
export function pdfLibroIvaTableWidth(pageW: number, marginX: number): number {
  return pageW - marginX * 2;
}

export type PdfFootCellDef = {
  content: string;
  colSpan?: number;
  styles?: { halign?: 'left' | 'right'; fontStyle?: 'bold'; fontSize?: number; cellPadding?: number };
};

/** Encabezados resumen por Tipo IVA — Libro IVA Ventas PDF (7 cols). */
export const PDF_LIBRO_IVA_VENTAS_RESUMEN_HEAD = [
  'Tipo IVA', 'Neto Grav.', 'IVA 21%', 'IVA 10.5%', 'IVA 27%', 'Exento', 'Total'
] as const;

/** Anchos del resumen por Tipo IVA; alineados con columnas numéricas del detalle. */
export function pdfLibroIvaVentasResumenColumnStyles(tableWidth: number) {
  const numericWidths = [48, 42, 42, 42, 42, 50];
  const numericTotal = numericWidths.reduce((sum, width) => sum + width, 0);
  const tipoIvaWidth = tableWidth - numericTotal;
  const numericStyle = (width: number) => ({
    halign: 'right' as const,
    cellWidth: width,
    overflow: 'linebreak' as const
  });

  return {
    0: { cellWidth: tipoIvaWidth },
    1: numericStyle(numericWidths[0]),
    2: numericStyle(numericWidths[1]),
    3: numericStyle(numericWidths[2]),
    4: numericStyle(numericWidths[3]),
    5: numericStyle(numericWidths[4]),
    6: numericStyle(numericWidths[5])
  };
}

/** Moneda compacta para PDF (sin espacio tras $) — columnas angostas del Libro IVA Compras. */
export function formatCurrencyPdfCompact(amount: number): string {
  if (!Number.isFinite(amount)) return '$0,00';
  const sign = amount < 0 ? '-' : '';
  const num = Math.abs(amount).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${sign}$${num}`;
}

/** Encabezados PDF Libro IVA Compras (18 cols) — textos cortos para evitar cortes. */
export const PDF_LIBRO_IVA_COMPRAS_HEAD = [
  'Fecha', 'Tipo', 'P.Vta', 'Número', 'Proveedor', 'CUIT', 'T.IVA',
  'Neto Grav.', 'IVA 21%', 'IVA 10.5%', 'IVA 27%', 'Exento',
  'P.IIBB', 'P.IVA', 'Imp.Int.', 'Imp.Mun.', 'Imp.Nac.', 'Total'
] as const;

/** Estilos base autoTable para Libro IVA Compras (sin ellipsize en importes). */
export function pdfLibroIvaComprasTableStyles() {
  return {
    styles: {
      fontSize: 5,
      cellPadding: 0.6,
      overflow: 'linebreak' as const,
      valign: 'middle' as const,
      minCellHeight: 10
    },
    headStyles: {
      fillColor: [245, 158, 11] as [number, number, number],
      textColor: 255,
      fontSize: 5,
      cellPadding: 0.8,
      overflow: 'linebreak' as const,
      valign: 'middle' as const
    },
    bodyStyles: { fontSize: 5, overflow: 'linebreak' as const }
  };
}

/** Tamaño de fuente del pie (totales). */
export const PDF_LIBRO_IVA_FOOT_LABEL_FONT_SIZE = 5.5;
export const PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_VENTAS = 5.5;
export const PDF_LIBRO_IVA_FOOT_AMOUNT_FONT_SIZE_COMPRAS = 5.5;

export function pdfLibroIvaFootStyles(amountFontSize: number) {
  return {
    fillColor: [240, 244, 248] as [number, number, number],
    textColor: [0, 0, 0] as [number, number, number],
    fontStyle: 'bold' as const,
    fontSize: amountFontSize,
    cellPadding: 0.5,
    overflow: 'linebreak' as const
  };
}

export function pdfFootLabelCell(content: string, colSpan: number): PdfFootCellDef {
  return {
    content,
    colSpan,
    styles: { halign: 'left', fontStyle: 'bold', fontSize: PDF_LIBRO_IVA_FOOT_LABEL_FONT_SIZE, cellPadding: 0.5 }
  };
}

export function pdfFootAmountCell(content: string, fontSize: number): PdfFootCellDef {
  return {
    content,
    styles: { halign: 'right', fontStyle: 'bold', fontSize, cellPadding: 0.4 }
  };
}

/** Anchos de columna Libro IVA Ventas (13 cols); deben sumar tableWidth. */
export function pdfLibroIvaVentasColumnStyles(tableWidth: number) {
  const cliente = tableWidth - (40 + 72 + 28 + 44 + 50 + 34 + 48 + 42 + 42 + 42 + 42 + 50);
  return {
    0: { cellWidth: 40 },
    1: { cellWidth: 72 },
    2: { cellWidth: 28 },
    3: { cellWidth: 44 },
    4: { cellWidth: cliente },
    5: { cellWidth: 50 },
    6: { cellWidth: 34 },
    7: { halign: 'right' as const, cellWidth: 48 },
    8: { halign: 'right' as const, cellWidth: 42 },
    9: { halign: 'right' as const, cellWidth: 42 },
    10: { halign: 'right' as const, cellWidth: 42 },
    11: { halign: 'right' as const, cellWidth: 42 },
    12: { halign: 'right' as const, cellWidth: 50 }
  };
}

/** Anchos de columna Libro IVA Compras (18 cols); deben sumar tableWidth. */
export function pdfLibroIvaComprasColumnStyles(tableWidth: number) {
  const tipoIvaWidth = 38;
  const textFixed = 32 + 48 + 20 + 34 + 40 + tipoIvaWidth;
  const numericWidth = 40;
  const numericTotal = numericWidth * 11;
  const proveedor = tableWidth - textFixed - numericTotal;
  const numericStyle = {
    halign: 'right' as const,
    cellWidth: numericWidth,
    fontSize: 5,
    overflow: 'linebreak' as const,
    cellPadding: 0.5
  };

  return {
    0: { cellWidth: 32 },
    1: { cellWidth: 48, overflow: 'ellipsize' as const },
    2: { cellWidth: 20 },
    3: { cellWidth: 34 },
    4: { cellWidth: proveedor, overflow: 'ellipsize' as const },
    5: { cellWidth: 40 },
    6: { cellWidth: tipoIvaWidth, overflow: 'ellipsize' as const },
    7: numericStyle,
    8: numericStyle,
    9: numericStyle,
    10: numericStyle,
    11: numericStyle,
    12: numericStyle,
    13: numericStyle,
    14: numericStyle,
    15: numericStyle,
    16: numericStyle,
    17: numericStyle
  };
}
